import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchDemoLinks } from './api'
import { useStaffToken } from './staffToken'

/**
 * Adopts the demo tenant's staff token when the API offers one.
 *
 * `GET /api/demo/links` 404s unless `FITTING_ROOM_DEMO_LINKS` is set, so this
 * does nothing at all in a deployment that is not a demo — there is no path by
 * which a real console skips its gate. Where it is on, the credential is
 * already on the page by design, and making a trial visitor copy it out of a
 * link board before they can look at a pull sheet is friction with no security
 * behind it.
 *
 * An existing stored token wins: someone who typed their own is not overwritten.
 */
export function useAdoptDemoStaffToken(): void {
  const { token, setToken } = useStaffToken()
  // Same key as the index page's board, so the two share one request.
  const demo = useQuery({
    queryKey: ['demo', 'links'] as const,
    queryFn: ({ signal }) => fetchDemoLinks(signal),
    retry: false,
    staleTime: Infinity,
  })
  const offered = demo.data?.staffToken

  useEffect(() => {
    if (!token && offered) setToken(offered)
  }, [token, offered, setToken])
}
